
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { findRedirect } from '@/lib/redirects';
import { useNursingHome } from '@/hooks/use-nursing-home';
import { toast } from "sonner";

interface RedirectMiddlewareProps {
  children: React.ReactNode;
}

/**
 * Component that handles client-side redirects
 * Sits at the root of the application to intercept navigation
 */
export const RedirectMiddleware: React.FC<RedirectMiddlewareProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hasChecked, setHasChecked] = useState(false);
  
  // For nursing home lookup redirects
  const isNursingHomeLookup = location.pathname.startsWith('/nursing-homes/lookup/');
  const nhSlug = isNursingHomeLookup ? location.pathname.split('/').pop() : undefined;
  const { data: nursingHome } = useNursingHome(nhSlug);

  // Handle nursing home lookup redirects
  useEffect(() => {
    if (isNursingHomeLookup && nursingHome && nursingHome.county_slug) {
      const newPath = `/nursing-homes/${nursingHome.county_slug}/${nursingHome.nh_slug}`;
      console.log(`Redirecting from lookup to: ${newPath}`);
      navigate(newPath, { replace: true });
    }
  }, [isNursingHomeLookup, nursingHome, navigate]);

  // Handle regular redirects
  useEffect(() => {
    if (!isNursingHomeLookup && !hasChecked) {
      const { redirectTo, isDynamic } = findRedirect(location.pathname);
      
      if (redirectTo) {
        console.log(`Redirecting from ${location.pathname} to ${redirectTo}`);
        
        // For dynamic redirects that may need further processing
        if (isDynamic && redirectTo.includes('/lookup/')) {
          // The lookup will be handled by the other useEffect
          navigate(redirectTo, { replace: true });
        } else {
          // Regular redirect
          navigate(redirectTo, { replace: true });
          
          // Only show toast for non-lookup redirects
          toast.info("Page moved: You've been redirected to the new location.", {
            duration: 3000,
          });
        }
      }
      
      setHasChecked(true);
    }
  }, [location.pathname, navigate, hasChecked, isNursingHomeLookup]);

  // Reset check when pathname changes
  useEffect(() => {
    setHasChecked(false);
  }, [location.pathname]);

  // Analytics tracking for redirects
  useEffect(() => {
    if (window.gtag && !hasChecked) {
      const { redirectTo } = findRedirect(location.pathname);
      if (redirectTo) {
        window.gtag('event', 'redirect', {
          event_category: 'Navigation',
          event_label: `${location.pathname} -> ${redirectTo}`,
        });
      }
    }
  }, [location.pathname, hasChecked]);

  return <>{children}</>;
};

export default RedirectMiddleware;
